import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, ArrowLeft, Loader2, Rocket, Bot, Send, Paperclip,
  Image as ImageIcon, Video, Calculator, MapPin, UploadCloud,
  Pencil, Trash2, Plus, Ruler, FileSignature, ExternalLink, Copy, FileText,
} from "lucide-react";
import { useAiJobBuilder } from "@/hooks/useAiJobBuilder";
import { VoiceMicButton } from "@/components/ai-builder/VoiceMicButton";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

function AssistantBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Bot className="h-4 w-4" />
      </div>
      <div className="max-w-3xl rounded-2xl border border-border bg-card px-4 py-3 text-sm leading-6 shadow-sm whitespace-pre-wrap">
        {children}
      </div>
    </div>
  );
}

function UserBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-3xl rounded-2xl bg-primary px-4 py-3 text-sm text-primary-foreground shadow-sm whitespace-pre-wrap">
        {children}
      </div>
    </div>
  );
}

function EditableScopeRow({
  item, onChange, onRemove,
}: {
  item: { trade: string; description: string; confidence: string };
  onChange: (patch: Partial<{ trade: string; description: string }>) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {editing ? (
            <div className="space-y-2">
              <Input value={item.trade} onChange={(e) => onChange({ trade: e.target.value })} placeholder="trade code" className="h-8 text-xs" />
              <Textarea value={item.description} onChange={(e) => onChange({ description: e.target.value })} className="min-h-[60px] text-sm" />
            </div>
          ) : (
            <>
              <div className="text-xs font-medium uppercase tracking-wide text-primary">{item.trade.replace(/_/g, " ")}</div>
              <div className="mt-1 text-sm text-foreground">{item.description}</div>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Badge variant="outline" className="text-[10px]">{item.confidence}</Badge>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing((v) => !v)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CreateJobAi() {
  const navigate = useNavigate();
  const b = useAiJobBuilder();
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [chatInput, setChatInput] = useState("");
  const [voicePartial, setVoicePartial] = useState("");
  const [voiceModeActive, setVoiceModeActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [measurementValues, setMeasurementValues] = useState<Record<string, any>>({});

  // Auto-scroll chat
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [b.chat.length, b.chatLoading, b.scopeItems.length, b.estimate, b.measurementRequest]);

  // Auto-notify AI ONLY when uploads finish — scope/estimate already reply via their own actions.
  const notify = b.notifyAi;
  const fileCount = b.files.length;
  const lastNotifiedFiles = useRef(0);
  useEffect(() => {
    if (fileCount > lastNotifiedFiles.current && fileCount > 0) {
      lastNotifiedFiles.current = fileCount;
      notify(`User just uploaded ${fileCount} file(s) total. They are ready for scope detection.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileCount]);

  const handleDroppedFiles = async (fileList: FileList | File[]) => {
    const arr = Array.from(fileList).filter(
      (f) =>
        f.type.startsWith("image/") ||
        f.type.startsWith("video/") ||
        f.type === "application/pdf" ||
        /\.pdf$/i.test(f.name),
    );
    if (arr.length) await b.uploadFiles(arr);
  };

  const canCreate = Boolean(b.ownerName.trim() && b.propertyAddress.trim());

  const submit = async () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatInput("");
    await b.sendChat(text);
  };

  const submitMeasurements = () => {
    if (!b.measurementRequest) return;
    b.saveMeasurement(b.measurementRequest.trade, measurementValues);
    setMeasurementValues({});
    b.notifyAi(`User just submitted measurements for ${b.measurementRequest.trade}: ${JSON.stringify(measurementValues)}.`);
  };

  return (
    <div className="flex h-screen w-screen flex-col bg-background overflow-hidden">
      <div className="border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="flex w-full items-center justify-between gap-4 px-2 sm:px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/jobs")} type="button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold">
                <Sparkles className="h-5 w-5 text-primary" /> Start Project with AI
              </h1>
              <p className="text-xs text-muted-foreground">Chat-first project intake — Bridgette drives scope, measurements, and estimate.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => b.createJob({ openEstimate: true })} disabled={b.creatingJob || !canCreate || !b.estimate} type="button">
              <Calculator className="mr-2 h-4 w-4" /> Open Full Estimate
            </Button>
            <Button onClick={() => b.createJob()} disabled={b.creatingJob || !canCreate} type="button">
              {b.creatingJob ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...</> : <><Rocket className="mr-2 h-4 w-4" /> Create Project</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid min-h-0 w-full flex-1 gap-4 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_420px] xl:grid-cols-[minmax(0,1fr)_460px]">
        <section
          className="relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-sm"
          onDragOver={(e) => { e.preventDefault(); if (!isDragging) setIsDragging(true); }}
          onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget as Node)) return; setIsDragging(false); }}
          onDrop={async (e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files?.length) await handleDroppedFiles(e.dataTransfer.files); }}
        >
          {isDragging && (
            <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/10 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2 text-primary">
                <UploadCloud className="h-12 w-12" />
                <p className="text-lg font-semibold">Drop photos, video, or blueprint PDF</p>
              </div>
            </div>
          )}

          <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto px-4 py-5 md:px-6">
            {b.chat.map((m) => (
              m.role === "assistant" ? <AssistantBubble key={m.id}>{m.content}</AssistantBubble> : <UserBubble key={m.id}>{m.content}</UserBubble>
            ))}

            {b.chatLoading && (
              <AssistantBubble>
                <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…</div>
              </AssistantBubble>
            )}
          </div>

          <div className="border-t border-border bg-background/95 p-3 backdrop-blur">
            <input
              ref={inputRef} type="file" multiple accept="image/*,video/*,application/pdf,.pdf" className="hidden"
              onChange={async (event) => {
                const selected = Array.from(event.target.files || []);
                if (selected.length) await b.uploadFiles(selected);
                event.target.value = "";
              }}
            />

            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mb-3 flex w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border bg-muted/10 px-4 py-3 text-center transition hover:border-primary hover:bg-primary/5"
            >
              <UploadCloud className="h-5 w-5 text-primary" />
              <p className="text-xs font-medium text-foreground">Drag & drop photos, video, or blueprint PDF — or click to browse</p>
              <p className="text-[10px] text-muted-foreground">Bridgette reads blueprints and pulls every trade + scope item automatically</p>
            </button>

            {b.files.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {b.files.map((file) => {
                  const Icon =
                    file.kind === "video" ? Video : file.kind === "blueprint" ? FileText : ImageIcon;
                  const blueprintLabel =
                    file.kind === "blueprint"
                      ? file.blueprintError
                        ? ` · ${file.blueprintError}`
                        : file.blueprintExtracted
                          ? ` · ${file.blueprintScopeCount ?? 0} items · ${file.blueprintSheetCount ?? "✓"} sheets read`
                          : " · reading…"
                      : "";
                  return (
                    <button
                      key={file.id}
                      type="button"
                      onClick={() => b.removeFile(file.id)}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs hover:bg-muted/40 ${
                        file.kind === "blueprint"
                          ? file.blueprintError
                            ? "border-destructive/40 bg-destructive/10 text-destructive"
                            : "border-primary/40 bg-primary/10 text-primary"
                          : "border-border bg-muted/20 text-muted-foreground"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span>{file.name}{blueprintLabel}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex items-end gap-2">
              <Button type="button" variant="outline" size="icon" className="h-[60px] w-12 shrink-0" onClick={() => inputRef.current?.click()}>
                <Paperclip className="h-4 w-4" />
              </Button>
              <div className="relative flex-1">
                <Textarea
                  value={voicePartial ? `${chatInput}${chatInput && !chatInput.endsWith(" ") ? " " : ""}${voicePartial}` : chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                  placeholder={voiceModeActive ? "Voice mode is live — speak naturally and Bridgette will reply in chat…" : "Talk to Bridgette — type or start voice mode…"}
                  className="min-h-[60px] resize-none pr-2"
                />
                {voicePartial && (
                  <span className="pointer-events-none absolute bottom-1 right-2 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-medium text-destructive">listening…</span>
                )}
              </div>
              <VoiceMicButton
                onPartial={setVoicePartial}
                onActiveChange={setVoiceModeActive}
                onTranscriptTurn={async (text) => {
                  setVoicePartial("");
                  await b.sendChat(text);
                }}
              />
              <Button type="button" size="icon" className="h-[60px] w-12 shrink-0" onClick={submit} disabled={!chatInput.trim() || b.chatLoading}>
                {b.chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </section>

        <aside className="min-h-0 space-y-4 overflow-y-auto">
          <Card>
            <CardContent className="space-y-4 p-4">
              <div>
                <p className="text-sm font-semibold text-foreground">Project snapshot</p>
                <p className="text-xs text-muted-foreground">Stays in sync with the chat.</p>
              </div>
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-border bg-muted/20 px-3 py-3 space-y-1">
                  <Label htmlFor="snap-owner" className="text-xs uppercase tracking-wide text-muted-foreground">Owner</Label>
                  <Input
                    id="snap-owner"
                    value={b.ownerName}
                    onChange={(e) => b.setOwnerName(e.target.value)}
                    placeholder="John Smith"
                    className="h-9 bg-background"
                  />
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-3 py-3 space-y-1">
                  <Label htmlFor="snap-address" className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> Property
                  </Label>
                  <Input
                    id="snap-address"
                    value={b.propertyAddress}
                    onChange={(e) => b.setPropertyAddress(e.target.value)}
                    placeholder="123 Main St, Dallas, TX"
                    className="h-9 bg-background"
                  />
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-3 py-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Trades</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {b.selectedTrades.length > 0 ? b.selectedTrades.map((t) => (
                      <Badge key={t} variant="secondary" className="capitalize">{t.replace(/_/g, " ")}</Badge>
                    )) : <span className="text-sm text-muted-foreground">none yet</span>}
                  </div>
                </div>
                {Object.keys(b.measurements).length > 0 && (
                  <div className="rounded-lg border border-border bg-muted/20 px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Measurements</div>
                    {Object.entries(b.measurements).map(([trade, vals]) => (
                      <div key={trade} className="text-xs">
                        <span className="font-medium capitalize">{trade.replace(/_/g, " ")}: </span>
                        <span className="text-muted-foreground">{Object.entries(vals).map(([k, v]) => `${k}=${v}`).join(", ")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Editable scope */}
          {b.scopeItems.length > 0 && (
            <Card>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-foreground">Detected scope</p>
                  <Button type="button" size="sm" variant="outline" onClick={() => b.addScopeItem({ trade: b.selectedTrades[0] || "general_construction", description: "New scope item", confidence: "medium" })}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {b.scopeItems.map((item, i) => (
                    <EditableScopeRow
                      key={`${item.trade}-${i}`}
                      item={item}
                      onChange={(p) => b.updateScopeItem(i, p as any)}
                      onRemove={() => b.removeScopeItem(i)}
                    />
                  ))}
                </div>
                <Button type="button" size="sm" className="w-full" onClick={() => b.generateEstimate()} disabled={b.estimateLoading || b.selectedTrades.length === 0}>
                  {b.estimateLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />}
                  Build / Rebuild Estimate
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Measurement request */}
          {b.measurementRequest && (
            <Card>
              <CardContent className="space-y-3 p-4">
                <p className="font-semibold text-foreground flex items-center gap-2">
                  <Ruler className="h-4 w-4 text-primary" />
                  Measurements: <span className="capitalize">{b.measurementRequest.trade.replace(/_/g, " ")}</span>
                </p>
                <div className="grid gap-2">
                  {b.measurementRequest.fields.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-xs">{f.label}</Label>
                      <Input
                        type={f.type === "number" ? "number" : "text"}
                        value={measurementValues[f.key] ?? ""}
                        onChange={(e) => setMeasurementValues((p) => ({ ...p, [f.key]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
                <Button type="button" size="sm" className="w-full" onClick={submitMeasurements}>Save measurements</Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-2 font-semibold text-foreground">
                <Calculator className="h-4 w-4" /> Live estimate
              </div>
              {!b.estimate ? (
                <p className="text-sm text-muted-foreground">Estimate will appear here once Bridgette builds it.</p>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-lg border border-border bg-muted/20 px-2 py-2">
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Sell</div>
                      <div className="text-sm font-semibold">{fmt(b.estimate.grand_total)}</div>
                    </div>
                    <div className="rounded-lg border border-border bg-muted/20 px-2 py-2">
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Cost</div>
                      <div className="text-sm font-semibold">{fmt(b.estimate.total_cost)}</div>
                    </div>
                    <div className="rounded-lg border border-border bg-muted/20 px-2 py-2">
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Profit</div>
                      <div className="text-sm font-semibold text-primary">{fmt(b.estimate.total_profit)}</div>
                    </div>
                  </div>
                  {b.estimate.trades.map((trade) => (
                    <div key={trade.trade_code} className="rounded-lg border border-border px-3 py-3">
                      <div className="flex items-center justify-between gap-3 border-b border-border/60 pb-2 mb-2">
                        <span className="font-semibold capitalize text-foreground text-sm">{trade.trade_code.replace(/_/g, " ")}</span>
                        <span className="text-sm font-bold text-primary">{fmt(trade.subtotal)}</span>
                      </div>
                      <div className="space-y-2">
                        {trade.line_items.map((item, i) => (
                          <div key={i} className="rounded border border-border/40 bg-muted/10 px-2.5 py-2">
                            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-start">
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-foreground leading-snug break-words">{item.name}</div>
                                {item.notes && <div className="mt-1 text-[10px] leading-relaxed text-muted-foreground break-words">{item.notes}</div>}
                                <div className="mt-1 text-[10px] text-muted-foreground">{item.qty} {item.unit} @ {fmt(item.unit_cost)}/{item.unit}</div>
                              </div>
                              <div className="rounded-md border border-border/50 bg-background px-2 py-1 text-xs font-semibold text-foreground whitespace-nowrap">{fmt(item.customer_price)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button type="button" size="sm" onClick={() => b.buildProposal("tiered")} disabled={b.proposalLoading || !canCreate}>
                      {b.proposalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSignature className="mr-2 h-4 w-4" />}
                      3 Tiers
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => b.buildProposal("single")} disabled={b.proposalLoading || !canCreate}>
                      <FileSignature className="mr-2 h-4 w-4" /> Single
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Built proposal */}
          {b.proposal && (
            <Card>
              <CardContent className="space-y-3 p-4">
                <p className="font-semibold text-foreground flex items-center gap-2">
                  <FileSignature className="h-4 w-4 text-primary" /> Proposal is live
                </p>
                <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Title</div>
                  <div className="text-sm font-semibold">{b.proposal.title}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-wide text-muted-foreground">Customer share link</div>
                  <div className="text-xs break-all text-foreground/80">{`${window.location.origin}/digital-proposal/${b.proposal.share_id}`}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" size="sm" onClick={() => window.open(`/digital-proposal/${b.proposal!.share_id}`, "_blank")}>
                    <ExternalLink className="mr-2 h-4 w-4" /> Open
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/digital-proposal/${b.proposal!.share_id}`)}>
                    <Copy className="mr-2 h-4 w-4" /> Copy
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="col-span-2" onClick={() => navigate(`/digital-proposals/review/${b.proposal!.id}`)}>
                    Review & Send
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
